import type { AxiosError } from 'axios'
import throttle from 'lodash/throttle'
import React from 'react'
import * as z from 'zod'

import type { SupportedMarketKey } from '@nxt-insurance/constants'

import type { Option } from '@ui-kit/components/formComponents/Select/types'

export type AddressDetail = {
  street: string
  houseNumber: string
  city: string
  country: string
  postalCode: string
  url: string
  lat: number
  lng: number
  image: string
}

export type Service = {
  get: <T>(endpoint: string) => Promise<{ data: T }>
}

export type Status =
  | {
      name: 'idle'
    }
  | {
      name: 'loading'
    }
  | {
      name: 'error'
      error_message: string
      status: string
    }
  | {
      name: 'success'
      predictions: Option[]
    }
  | {
      name: 'addressDetailLoaded'
    }

const predictionsSchema = z.object({
  predictions: z
    .array(
      z.object({
        description: z.string(),
        place_id: z.string(),
        types: z.array(z.string()),
      })
    )
    .optional()
    .default([]),
})

const errorSchema = z.object({
  error_message: z.string(),
  status: z.string(),
})

type Prediction = z.infer<typeof predictionsSchema>['predictions'][number]

type Error = z.infer<typeof errorSchema>

const THROTTLE_INTERVAL = 600
const MIN_QUERY_LENGTH = 3

export const useAddressAutocomplete = (service: Service) => {
  const [status, setStatus] = React.useState<Status>({ name: 'idle' })
  const requestIdRef = React.useRef(0)

  const loadPredictions = React.useMemo(
    () =>
      throttle(async (input: string, country: SupportedMarketKey) => {
        if (input.trim().length < MIN_QUERY_LENGTH) {
          requestIdRef.current += 1
          setStatus({ name: 'idle' })
          return
        }
        const requestId = (requestIdRef.current += 1)
        setStatus((prev) => (prev.name === 'success' ? prev : { name: 'loading' }))
        try {
          const result = await service.get<{ predictions: Prediction[] } | Error>(
            `v1/address_autocomplete?input=${encodeURIComponent(input)}&country=${country}`
          )
          // Ignore responses from superseded keystrokes so a slow request can't overwrite newer results.
          if (requestId !== requestIdRef.current) {
            return
          }
          const errorResult = errorSchema.safeParse(result.data)
          if (errorResult.success) {
            setStatus({
              name: 'error',
              error_message: errorResult.data.error_message,
              status: errorResult.data.status,
            })
            return
          }
          const predictionsResult = predictionsSchema.safeParse(result.data)
          if (predictionsResult.success) {
            setStatus({
              name: 'success',
              predictions: predictionsResult.data.predictions.map((prediction) => ({
                label: prediction.description,
                value: prediction.place_id,
              })),
            })
            return
          }
          setStatus({ name: 'error', error_message: 'Unexpected response', status: '' })
        } catch (error) {
          if (requestId !== requestIdRef.current) {
            return
          }
          const networkError = error as AxiosError
          setStatus({
            name: 'error',
            error_message: networkError.message,
            status: networkError.status ? networkError.status.toString() : '',
          })
        }
      }, THROTTLE_INTERVAL),
    [service]
  )

  const loadAddressDetail = React.useCallback(
    async (
      placeId: string,
      dimensions?: { width: string; height: string },
      withMap = true
    ): Promise<AddressDetail | null> => {
      setStatus({ name: 'loading' })
      try {
        const result = await service.get<{ result: Omit<AddressDetail, 'image'> } | Error>(
          `v1/address_autocomplete/details?placeid=${encodeURIComponent(placeId)}`
        )
        if (!('result' in result.data)) {
          setStatus({
            name: 'error',
            error_message: result.data.error_message,
            status: result.data.status,
          })
          return null
        }
        // The map image is presentational — a failed image must not discard a resolved address.
        let image = ''
        if (withMap) {
          try {
            const imgResult = await service.get<{ image: string }>(
              `v1/address_autocomplete/image?lat=${encodeURIComponent(String(result.data.result.lat))}&lng=${encodeURIComponent(String(result.data.result.lng))}`.concat(
                dimensions ? `&width=${dimensions.width}&height=${dimensions.height}` : ''
              )
            )
            image = imgResult.data.image
          } catch {
            // keep the resolved address; the summary renders without a map
          }
        }
        setStatus({ name: 'addressDetailLoaded' })
        return { ...result.data.result, image }
      } catch (error) {
        setStatus({
          name: 'error',
          error_message: `An unknown error occurred: ${error}.`,
          status: '',
        })
        return null
      }
    },
    [service]
  )

  const lookupAddressDetail = React.useCallback(
    async (
      input: string,
      country: SupportedMarketKey,
      dimensions?: { width: string; height: string }
    ): Promise<AddressDetail | null> => {
      if (!input) {
        return null
      }
      try {
        const result = await service.get<{ predictions: Prediction[] } | Error>(
          `v1/address_autocomplete?input=${encodeURIComponent(input)}&country=${country}`
        )
        const predictionsResult = predictionsSchema.safeParse(result.data)
        if (!predictionsResult.success || predictionsResult.data.predictions.length === 0) {
          return null
        }
        return await loadAddressDetail(predictionsResult.data.predictions[0].place_id, dimensions)
      } catch {
        return null
      }
    },
    [service, loadAddressDetail]
  )

  return {
    status,
    loadPredictions,
    loadAddressDetail,
    lookupAddressDetail,
  }
}
